/**
 * WC-07: Day Challenge Card Component
 *
 * Displays a single day's challenge with status, preview, and actions.
 */

import React from 'react';
import {
  ChallengeMode,
  DayStatus,
  DayResult,
  formatModeName,
  getModeIcon,
  getStatusIcon,
  formatTime,
  getStarDisplay
} from '../../stores/useChallengeStore';
import './DayChallengeCard.css';

interface Enemy {
  name: string;
  difficulty: string;
  count: number;
}

interface DayChallengeCardProps {
  day: number;
  name: string;
  mode: ChallengeMode;
  flavor: string;
  status: DayStatus;
  unlocksAt?: Date;
  attempts?: number;
  bestResult?: DayResult;
  enemies: Enemy[];
  onStart: () => void;
  onViewDetails: () => void;
}

export const DayChallengeCard: React.FC<DayChallengeCardProps> = ({
  day,
  name,
  mode,
  flavor,
  status,
  unlocksAt,
  attempts = 0,
  bestResult,
  enemies,
  onStart,
  onViewDetails,
}) => {
  const getDayLabel = () => {
    const days = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days[day] || '';
  };

  const formatUnlockDate = (date: Date) => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return `in ${hours}h`;
    }
    return getDayLabel();
  };

  return (
    <div
      className={`day-challenge-card ${status}`}
      onClick={status !== 'locked' ? onViewDetails : undefined}
    >
      {/* Header */}
      <div className="day-card-header">
        <span className="day-number">Day {day}</span>
        <span className="day-label">{getDayLabel()}</span>
        <span className="status-icon">{getStatusIcon(status)}</span>
      </div>

      {/* Name */}
      <h3 className="day-name">{name}</h3>

      {/* Mode Badge */}
      <div className="mode-badge">
        <span className="mode-icon">{getModeIcon(mode)}</span>
        <span className="mode-name">{formatModeName(mode)}</span>
      </div>

      {/* Enemy Preview */}
      <div className="enemy-preview">
        {enemies.slice(0, 3).map((enemy, idx) => (
          <span key={idx} className={`enemy-tag ${enemy.difficulty}`}>
            {enemy.name}
          </span>
        ))}
        {enemies.length > 3 && (
          <span className="enemy-more">+{enemies.length - 3} more</span>
        )}
      </div>

      {/* Best Result (for completed days) */}
      {status === 'completed' && bestResult && (
        <div className="best-result">
          <div className="stars">{getStarDisplay(bestResult.stars)}</div>
          <div className="best-time">{formatTime(bestResult.turnsUsed)}</div>
        </div>
      )}

      {/* In Progress indicator */}
      {status === 'in_progress' && (
        <div className="progress-indicator">
          <span className="attempts">Attempts: {attempts}</span>
        </div>
      )}

      {/* Actions */}
      <div className="card-actions">
        {status === 'available' && (
          <button
            className="start-button"
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
          >
            START
          </button>
        )}

        {status === 'in_progress' && (
          <button
            className="continue-button"
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
          >
            CONTINUE
          </button>
        )}

        {status === 'completed' && (
          <button
            className="replay-button"
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
          >
            REPLAY
          </button>
        )}

        {status === 'locked' && unlocksAt && (
          <div className="unlock-info">
            Unlocks {formatUnlockDate(unlocksAt)}
          </div>
        )}
      </div>
    </div>
  );
};
